// ***************************************
// ComponentPicture
//
// This component displays an animated picture
//
// Copyright 2015 Tingly Games B.V.
// Last Changed: 18-6-2015
// ***************************************
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { ComponentBase } from "./ComponentBase";
import { tgfSettings } from "../tgfSettings";
import { s_blackpixel } from "../../code/AssetDefinitions/ImageDefinitions";
tgfSettings.componentDefaults.picture = {
    image: s_blackpixel,
    mask: undefined,
    animationSpeed: 0,
    startSubImage: 0,
    loopAnimation: true,
    roundSubImage: true,
    createFromSlices: false,
    sliceMargins: 10,
    sliceMarginsArray: [],
    onAnimationComplete: null,
    roundPosition: true,
    maintainAspectRatio: true,
    components: [],
    onClick: null,
    tint: 0xFFFFFF // The tint applied to the sprite. This is a hex value. A value of 0xFFFFFF will remove any tint effect.
};
var ComponentPicture = /** @class */ (function (_super) {
    __extends(ComponentPicture, _super);
    function ComponentPicture(properties) {
        var _this = _super.call(this) || this;
        _this.name = "Picture";
        // Initialize properties
        _this.initialize(tgfSettings.componentDefaults.picture);
        // Set user properties
        if (properties !== undefined) {
            _this.setProperties(properties);
        }
        return _this;
    }
    // Overriden to return the correct width
    ComponentPicture.prototype.getWidth = function () {
        if (this.width > 0) {
            return this.width;
        }
        else if (!this.image) {
            return 0;
        }
        else {
            return this.image.width * this.image.scaleX;
        }
    };
    // Overriden to return the correct width
    ComponentPicture.prototype.getHeight = function () {
        if (this.height > 0) {
            return this.height;
        }
        else if (!this.image) {
            return 0;
        }
        else {
            return this.image.height * this.image.scaleY;
        }
    };
    // ***************************************
    // Preparing, showing and activating
    // ***************************************
    // Processing required when preparing the component for use
    ComponentPicture.prototype.doPrepare = function () {
        var _this = this;
        if (!this.image) {
            throw new Error("ComponentPicture \"".concat(this.name, "\" has no image"));
        }
        this.image = this.image.clone();
        if (this.createFromSlices) {
            var xOrigin = this.image.xOrigin, yOrigin = this.image.yOrigin;
            if (this.sliceMarginsArray.length > 0) {
                this.image = this.image.getNineSlice(0, this.sliceMarginsArray[0], this.sliceMarginsArray[1], this.sliceMarginsArray[2], this.sliceMarginsArray[3]);
            }
            else {
                this.image = this.image.getNineSlice(0, this.sliceMargins, this.sliceMargins, this.sliceMargins, this.sliceMargins);
            }
            this.image.xOrigin = xOrigin;
            this.image.yOrigin = yOrigin;
        }
        // Add the child components
        for (var i = 0; i < this.components.length; i += 1) {
            this.addComponent(this.components[i]);
        }
        this.image.add(this.baseContainer);
        if (this.mask) {
            this.baseContainer.addChild(this.mask);
            this.mask.position.set(this.containerRelativeX(), this.containerRelativeY());
            this.image.setMask(this.mask);
        }
        this.image.setScale(this.xScale, this.yScale);
        if (this.onClick) {
            this.image.setCallback(function () {
                if (!_this.isActive || !_this.isVisible) {
                    return;
                }
                _this.onClick();
            }, true);
        }
        this.image.setRotation(this.angle);
        this.image.setAlpha(this.alpha);
        this.image.setTint(this.tint);
        this.image.setDimensions(this.getWidth(), this.getHeight());
    };
    ComponentPicture.prototype.changeImage = function (image) {
        if (this.image && image) {
            this.image.remove(this.baseContainer);
            this.image.free();
            this.image = image;
            this.doPrepare();
        }
    };
    ComponentPicture.prototype.bringToFront = function () {
        this.image.bringToFront();
    };
    ComponentPicture.prototype.setDimensions = function (width, height) {
        this.width = width;
        this.height = height;
        this.image.setDimensions(width, height);
    };
    ComponentPicture.prototype.addSubImage = function (imageName) {
        // Assuming we have tbImage when this functions is used
        this.image.addImage(imageName);
    };
    // Processing required when showing the component
    ComponentPicture.prototype.setSubImage = function (index) {
        var frame = Math.min(index, this.image.sprites.length - 1);
        this.image.gotoAndStop(frame);
    };
    ComponentPicture.prototype.setTint = function (tint) {
        this.tint = tint;
        this.image.setTint(this.tint);
    };
    ComponentPicture.prototype.setAlpha = function (alpha) {
        this.alpha = alpha;
        this.image.setAlpha(this.alpha);
    };
    ComponentPicture.prototype.setAngle = function (angle) {
        this.angle = angle;
        this.image.setRotation(this.angle);
    };
    ComponentPicture.prototype.doShow = function () {
        this.image.show();
    };
    ComponentPicture.prototype.doHide = function () {
        this.image.hide();
    };
    ComponentPicture.prototype.setScale = function (scaleX, scaleY) {
        this.xScale = scaleX;
        this.yScale = scaleY;
        this.image.setScale(this.xScale, this.yScale);
    };
    ComponentPicture.prototype.doFree = function () {
        if (this.image) {
            this.image.remove(this.baseContainer);
            this.image.free();
            this.image = null;
        }
    };
    // ***************************************
    // Methods called when events must be processed
    // ***************************************
    // Called when the component must be drawn (relative to position 0, 0)
    ComponentPicture.prototype.doDraw = function () {
        var ww, hh, xx, yy;
        if (this.image === null) {
            return;
        }
        ww = this.width > 0 ? this.width : this.image.width;
        hh = this.height > 0 ? this.height : this.image.height;
        xx = -ww / 2;
        yy = -hh / 2;
        if (ww === this.image.width && hh === this.image.height) {
            this.image.setPosition(this.containerRelativeX(), this.containerRelativeY());
        }
    };
    return ComponentPicture;
}(ComponentBase));
export { ComponentPicture };
